import iCloud from "../../../assets/icons/hero/cloud.webp";
import iWave1 from "../../../assets/icons/hero/wave-1.webp";
import iWave2 from "../../../assets/icons/hero/wave-2.webp";
import iWave3 from "../../../assets/icons/hero/wave-3.webp";
import iRectangle1 from "../../../assets/icons/hero/rectangle-1.svg";
import iRectangle2 from "../../../assets/icons/hero/rectangle-2.svg";
import iRectangle3 from "../../../assets/icons/hero/rectangle-3.svg";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useScrollContext } from "../../../lib/scroll";
import WavingDuck from "./waving-duck/waving-duck";
import { Parallax } from "react-scroll-parallax";
import { MOBILE_WIDTH_PX } from "../../../const/const";
import "./hero-block.sass";

const HeroBlock = () => {
  const scrollContext = useScrollContext();
  const [isMobileWidth, setIsMobileWidth] = useState(
    window.innerWidth <= MOBILE_WIDTH_PX,
  );
  const heroBlockRef = useRef<HTMLDivElement>(null);
  const cloudLeftRef = useRef<HTMLImageElement>(null);
  const cloudMidRef = useRef<HTMLImageElement>(null);
  const cloudRightRef = useRef<HTMLImageElement>(null);
  const waveBackRef = useRef<HTMLImageElement>(null);
  const waveMidRef = useRef<HTMLImageElement>(null);
  const waveFrontRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileWidth(window.innerWidth <= MOBILE_WIDTH_PX);
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const tweens: gsap.core.Tween[] = [];

    const addTween = (
      target: HTMLImageElement | null,
      vars: gsap.TweenVars,
    ) => {
      if (!target) {
        return;
      }

      tweens.push(
        gsap.to(target, {
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          ...vars,
        }),
      );
    };

    addTween(cloudLeftRef.current, { x: 10, duration: 2.8 });
    addTween(cloudMidRef.current, { x: -8, duration: 2.4, delay: 0.1 });
    addTween(cloudRightRef.current, { x: 8, duration: 2.1, delay: 0.2 });
    addTween(waveBackRef.current, { x: 26, duration: 3.6 });
    addTween(waveMidRef.current, { x: -20, duration: 3.1, delay: 0.15 });
    addTween(waveFrontRef.current, { x: 14, duration: 2.7, delay: 0.25 });

    return () => {
      tweens.forEach((tween) => tween.kill());
    };
  }, [isMobileWidth]);

  useEffect(() => {
    const getCloudNodes = () =>
      [cloudLeftRef.current, cloudMidRef.current, cloudRightRef.current].filter(
        (node): node is HTMLImageElement => node !== null,
      );

    if (!isMobileWidth) {
      const cloudNodes = getCloudNodes();
      if (cloudNodes.length > 0) {
        gsap.set(cloudNodes, { clearProps: "y" });
      }
      return;
    }

    const leftTo = cloudLeftRef.current
      ? gsap.quickTo(cloudLeftRef.current, "y", {
          duration: 0.35,
          ease: "power2.out",
        })
      : null;
    const midTo = cloudMidRef.current
      ? gsap.quickTo(cloudMidRef.current, "y", {
          duration: 0.35,
          ease: "power2.out",
        })
      : null;
    const rightTo = cloudRightRef.current
      ? gsap.quickTo(cloudRightRef.current, "y", {
          duration: 0.35,
          ease: "power2.out",
        })
      : null;

    const handleScrollFrame = (amount: number, _delta: number) => {
      leftTo?.(75 + Math.min(amount * 0.18, 160));
      midTo?.(10 + Math.min(amount * 0.16, 145));
      rightTo?.(85 + Math.min(amount * 0.14, 130));
    };

    handleScrollFrame(window.scrollY, 0);
    scrollContext?.bindScrollHandler(handleScrollFrame);

    return () => {
      scrollContext?.unbindScrollHandler(handleScrollFrame);
      const cloudNodes = getCloudNodes();
      if (cloudNodes.length > 0) {
        gsap.set(cloudNodes, { clearProps: "y" });
      }
    };
  }, [isMobileWidth, scrollContext]);

  return (
    <div className="hero-block" ref={heroBlockRef}>
      <div className="hero-block__text">
        агентство, которое <br /> делает бренды <br /> частью среды
      </div>

      <WavingDuck />

      {isMobileWidth ? (
        <>
          <Parallax speed={30} translateY={["0px", "0px"]}>
            <img
              ref={cloudLeftRef}
              src={iCloud}
              alt=""
              className="hero-block__cloud hero-block__cloud-left"
            />
          </Parallax>

          <Parallax translateY={["0px", "0px"]} speed={30}>
            <img
              ref={cloudMidRef}
              src={iCloud}
              alt=""
              className="hero-block__cloud hero-block__cloud-mid"
            />
          </Parallax>

          <Parallax
            speed={30}
            translateY={["0px", "0px"]}
          >
            <img
              ref={cloudRightRef}
              src={iCloud}
              className="hero-block__cloud hero-block__cloud-right"
              alt=""
            />
          </Parallax>

          <Parallax
            speed={30}
            className="hero-block__wave hero-block__wave-back"
            translateX={["10px", "100px"]}
            translateY={["0px", "0px"]}
          >
            <img
              ref={waveBackRef}
              src={iWave3}
              className="hero-block__wave hero-block__wave-back"
              alt=""
            />
          </Parallax>

          <Parallax
            speed={15}
            className="hero-block__wave hero-block__wave-mid"
            translateX={["-50px", "100px"]}
            translateY={["0px", "0px"]}
          >
            <img
              ref={waveMidRef}
              src={iWave2}
              className="hero-block__wave hero-block__wave-mid"
              alt=""
            />
          </Parallax>

          <Parallax
            speed={5}
            className="hero-block__wave hero-block__wave-front"
            translateX={["-150px", "50px"]}
            translateY={["0px", "0px"]}
          >
            <img
              ref={waveFrontRef}
              src={iWave1}
              className="hero-block__wave hero-block__wave-front"
              alt=""
            />
          </Parallax>
        </>
      ) : (
        <>
          <Parallax speed={30} translateY={["-1000px", "200px"]}>
            <img
              ref={cloudLeftRef}
              src={iCloud}
              alt=""
              className="hero-block__cloud hero-block__cloud-left"
            />
          </Parallax>

          <Parallax translateY={["-240px", "120px"]} speed={30}>
            <img
              ref={cloudMidRef}
              src={iCloud}
              alt=""
              className="hero-block__cloud hero-block__cloud-mid"
            />
          </Parallax>

          <Parallax
            speed={30}
            translateY={["-40px", "120px"]}
          >
            <img
              ref={cloudRightRef}
              src={iCloud}
              className="hero-block__cloud hero-block__cloud-right"
              alt=""
            />
          </Parallax>

          <Parallax
            speed={30}
            className="hero-block__wave hero-block__wave-back"
            translateX={["10px", "100px"]}
            translateY={["0px", "0px"]}
          >
            <img
              ref={waveBackRef}
              src={iWave3}
              className="hero-block__wave hero-block__wave-back"
              alt=""
            />
          </Parallax>

          <Parallax
            speed={15}
            className="hero-block__wave hero-block__wave-mid"
            translateX={["-50px", "100px"]}
            translateY={["0px", "0px"]}
          >
            <img
              ref={waveMidRef}
              src={iWave2}
              className="hero-block__wave hero-block__wave-mid"
              alt=""
            />
          </Parallax>

          <Parallax
            speed={5}
            className="hero-block__wave hero-block__wave-front"
            translateX={["-150px", "50px"]}
            translateY={["0px", "0px"]}
          >
            <img
              ref={waveFrontRef}
              src={iWave1}
              className="hero-block__wave hero-block__wave-front"
              alt=""
            />
          </Parallax>
        </>
      )}

      <img
        src={iRectangle1}
        className="img-absolute hero-block__rectangle hero-block__rectangle-top"
        alt=""
      />

      <img
        src={iRectangle2}
        className="img-absolute hero-block__rectangle hero-block__rectangle-left"
        alt=""
      />

      <img
        src={iRectangle3}
        className="img-absolute hero-block__rectangle hero-block__rectangle-right"
        alt=""
      />
    </div>
  );
};

export default HeroBlock;
